import { useCallback, useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/useOrganization';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';

interface ShopifyConnection {
  status: string;
  shop_domain?: string;
  shop_name?: string;
  scopes?: string;
  installed_at?: string;
  disconnected_at?: string;
  billing_status?: string;
}

export function IntegrationSettings() {
  const { organization } = useOrganization();
  const { idToken } = useCognitoAuth();

  const [connection, setConnection] = useState<ShopifyConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [shopDomain, setShopDomain] = useState('');

  const fetchConnection = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<ShopifyConnection>('/api/shopify/connection', { errorCode: ErrorCode.FETCH_CONNECTION, token: idToken });
      setConnection(data);
    } catch (err) {
      setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to load connection');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    void fetchConnection();
  }, [fetchConnection]);

  // Check for status/error query params from OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const err = params.get('error');
    if (status === 'connected') {
      setError(null);
      void fetchConnection();
    } else if (err) {
      const messages: Record<string, string> = {
        hmac_failed: 'Security verification failed. Please try again.',
        expired_code: 'The authorization code has expired. Please try again.',
        invalid_state: 'The request state is invalid. Please try again.',
        token_exchange_failed: 'Could not exchange authorization code. Please try again.',
        storage_failed: 'Could not save connection. Please try again.',
      };
      setError(messages[err] || `Connection failed: ${err}`);
    }
    // Clean URL without reloading
    if (status || err) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchConnection]);

  const handleConnect = () => {
    const orgId = organization?.id;
    if (!orgId) return;
    const domain = shopDomain.trim() || prompt('Enter your Shopify store domain (e.g., my-store.myshopify.com)');
    if (!domain) return;
    window.location.href = `/api/shopify/install?shop=${encodeURIComponent(domain)}&org_id=${orgId}`;
  };

  const handleDisconnect = async () => {
    if (!idToken) return;
    setDisconnecting(true);
    try {
      await api.post('/api/shopify/connection/disconnect', {}, { errorCode: ErrorCode.DISCONNECT_CONNECTION, token: idToken });
      await fetchConnection();
      setDisconnectOpen(false);
    } catch (err) {
      setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Disconnect failed');
    } finally {
      setDisconnecting(false);
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'disconnected':
        return <Badge variant="secondary">Disconnected</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const billingBadge = (bs: string) => {
    switch (bs) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending Approval</Badge>;
      case 'cancelled':
        return <Badge variant="secondary">Cancelled</Badge>;
      case 'expired':
        return <Badge variant="secondary">Expired</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="py-4">
            <p className="text-destructive text-sm">{error}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Shopify Integration</CardTitle>
          <CardDescription>
            Connect your Shopify store to automatically import orders, products, and payouts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {connection && connection.status !== 'not_connected' ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <p className="mt-1">{statusBadge(connection.status)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Store</p>
                  <p className="mt-1 text-sm">{connection.shop_name ?? connection.shop_domain}</p>
                </div>
                {connection.installed_at && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Installed</p>
                    <p className="mt-1 text-sm">
                      {new Date(connection.installed_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {connection.disconnected_at && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Disconnected</p>
                    <p className="mt-1 text-sm">
                      {new Date(connection.disconnected_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {connection.billing_status && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Billing</p>
                    <p className="mt-1">{billingBadge(connection.billing_status)}</p>
                  </div>
                )}
                {connection.scopes && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Permissions</p>
                    <p className="mt-1 text-xs text-muted-foreground">{connection.scopes}</p>
                  </div>
                )}
              </div>

              {connection.status === 'active' && (
                <>
                  <Separator />
                  <div className="flex justify-end">
                    <Button
                      variant="destructive"
                      onClick={() => setDisconnectOpen(true)}
                    >
                      Disconnect Store
                    </Button>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <i className="bi bi-shop text-4xl text-muted-foreground"></i>
              <p className="mt-3 text-muted-foreground">No Shopify store connected</p>
              <p className="text-sm text-muted-foreground mt-1">
                Connect your store to import orders and manage your books
              </p>
              <div className="mt-4 max-w-sm mx-auto space-y-3">
                <Input
                  placeholder="my-store.myshopify.com"
                  value={shopDomain}
                  onChange={(e) => setShopDomain(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                />
                <Button className="w-full" onClick={handleConnect}>
                  <i className="bi bi-link-45deg mr-2"></i>
                  Connect Shopify Store
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Shopify Store</DialogTitle>
            <DialogDescription>
              This will remove the connection to <strong>{connection?.shop_name ?? connection?.shop_domain}</strong>.
              All previously imported orders, products, and payouts will be preserved in your BeanGo books.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDisconnectOpen(false)}
              disabled={disconnecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
